import React, { useState, useEffect } from 'react';
import { serialKeysAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function SerialKeys() {
  const { hasPermission, user } = useAuth();
  
  // Permission checks
  const canCreate = hasPermission('serial_keys.create');
  const canEdit = hasPermission('serial_keys.update');
  const canReset = hasPermission('serial_keys.reset');
  const canDelete = hasPermission('serial_keys.delete');

  const [showForm, setShowForm] = useState(false);
  const [showResetModal, setShowResetModal] = useState(null);
  const [showResetHistory, setShowResetHistory] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [formData, setFormData] = useState({
    serial_number: '',
    max_usage_count: 1,
    expires_at: '',
    notes: '',
  });
  const [resetData, setResetData] = useState({
    reason: '',
    admin_username: user?.username || 'admin',
  });
  const [resetHistory, setResetHistory] = useState([]);
  const [serialKeys, setSerialKeys] = useState([]);
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filters, setFilters] = useState({
    search: '',
    is_active: '',
  });

  useEffect(() => {
    fetchSerialKeys();
  }, [pagination.page, filters]);

  const fetchSerialKeys = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await serialKeysAPI.getSerialKeys(params);
      setSerialKeys(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Error fetching serial keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) : value
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!canCreate) {
      setMessage({ type: 'error', text: 'You do not have permission to create serial keys' });
      return;
    }
    
    if (!formData.serial_number.trim()) {
      setMessage({ type: 'error', text: 'Serial number is required' });
      return;
    }

    try {
      await serialKeysAPI.createSerialKey({
        serial_number: formData.serial_number,
        max_usage_count: formData.max_usage_count,
        expires_at: formData.expires_at || null,
        notes: formData.notes,
      });
      
      setMessage({ type: 'success', text: 'Serial key created successfully!' });
      setFormData({ serial_number: '', max_usage_count: 1, expires_at: '', notes: '' });
      setShowForm(false);
      fetchSerialKeys();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to create serial key' 
      });
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!canEdit) {
      setMessage({ type: 'error', text: 'You do not have permission to edit serial keys' });
      return;
    }
    
    if (!editMode) return;

    try {
      await serialKeysAPI.updateSerialKey(editMode.id, {
        max_usage_count: formData.max_usage_count,
        is_active: formData.is_active,
        expires_at: formData.expires_at || null,
      });
      
      setMessage({ type: 'success', text: 'Serial key updated successfully!' });
      setEditMode(null);
      setShowForm(false);
      fetchSerialKeys();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update serial key' 
      });
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    
    if (!canReset) {
      setMessage({ type: 'error', text: 'You do not have permission to reset serial keys' });
      return;
    }
    
    if (!resetData.reason || resetData.reason.trim().length < 10) {
      setMessage({ type: 'error', text: 'Reset reason must be at least 10 characters' });
      return;
    }

    if (!window.confirm(`⚠️ WARNING: This will reset the serial key and unlink ${showResetModal.user_count} users. Are you sure?`)) {
      return;
    }

    try {
      const response = await serialKeysAPI.resetSerialKey(showResetModal.id, resetData);
      
      setMessage({ 
        type: 'success', 
        text: `Serial key reset successfully! ${response.data.users_affected} users unlinked.` 
      });
      
      setResetData({ reason: '', admin_username: user?.username || 'admin' });
      setShowResetModal(null);
      fetchSerialKeys();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to reset serial key' 
      });
    }
  };

  const viewDetail = async (id) => {
    try {
      const response = await serialKeysAPI.getSerialKeyDetail(id);
      setSelectedSerial(response.data);
    } catch (error) {
      console.error('Error fetching detail:', error);
    }
  };

  const viewResetHistory = async (serial) => {
    try {
      const response = await serialKeysAPI.getResetHistory(serial.id);
      setResetHistory(response.data.history || []);
      setShowResetHistory(serial);
    } catch (error) {
      console.error('Error fetching reset history:', error);
    }
  };

  const startEdit = (serial) => {
    if (!canEdit) {
      setMessage({ type: 'error', text: 'You do not have permission to edit serial keys' });
      return;
    }
    setEditMode(serial);
    setFormData({
      max_usage_count: serial.max_usage_count,
      is_active: serial.is_active,
      expires_at: serial.expires_at ? serial.expires_at.slice(0, 16) : '',
    });
    setShowForm(true);
  };

  const startReset = (serial) => {
    if (!canReset) {
      setMessage({ type: 'error', text: 'You do not have permission to reset serial keys' });
      return;
    }
    setShowResetModal(serial);
    setResetData({ reason: '', admin_username: user?.username || 'admin' });
  };

  return (
    <div>
      <div className="page-header">
        <h1>시리얼 키 관리 </h1>
        <p>시리얼 정보 등록/수정/초기화</p>
        
        {/* Only show Register button if user has create permission */}
        {canCreate && (
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setShowForm(!showForm);
              setEditMode(null);
              setFormData({ serial_number: '', max_usage_count: 1, expires_at: '', notes: '' });
            }}
            style={{ marginTop: '10px' }}
          >
            {showForm ? 'Cancel' : '+ Register Serial Key'}
          </button>
        )}
      </div>

      {message.text && (
        <div className={message.type === 'success' ? 'success' : 'error'} style={{ marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {showForm && (canCreate || canEdit) && (
        <div className="card">
          <div className="card-header">
            <h2>{editMode ? 'Edit Serial Key' : 'Register New Serial Key'}</h2>
          </div>
          
          <form onSubmit={editMode ? handleUpdate : handleCreate}>
            {!editMode && (
              <div className="form-group">
                <label>Serial Number *</label>
                <input
                  type="text"
                  name="serial_number"
                  className="form-control"
                  placeholder="e.g., DEFAULT001, SN123456789"
                  value={formData.serial_number}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}

            {editMode && (
              <div className="form-group">
                <label>Serial Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={editMode.serial_number}
                  disabled
                  style={{ background: '#ecf0f1' }}
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Max Usage Count *</label>
                <input
                  type="number"
                  name="max_usage_count"
                  className="form-control"
                  min="1"
                  value={formData.max_usage_count}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Expires At</label>
                <input
                  type="datetime-local"
                  name="expires_at"
                  className="form-control"
                  value={formData.expires_at}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {editMode && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  Active
                </label>
              </div>
            )}

            {!editMode && (
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  className="form-control"
                  placeholder="Additional notes (optional)"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
            )}

            <button type="submit" className="btn btn-success">
              {editMode ? 'Update Serial Key' : 'Register Serial Key'}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>All Serial Keys</h2>
        </div>

        <div className="filters">
          <div className="filter-item">
            <input
              type="text"
              className="form-control"
              placeholder="Search by serial number"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          <div className="filter-item">
            <select
              className="form-control"
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Loading serial keys...</div>
        ) : serialKeys.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            No serial keys registered yet
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Serial Number</th>
                    <th>Max Usage</th>
                    <th>Current Usage</th>
                    <th>Usage %</th>
                    <th>User Count</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {serialKeys.map((serial) => (
                    <tr key={serial.id}>
                      <td>{serial.id}</td>
                      <td><strong>{serial.serial_number}</strong></td>
                      <td>{serial.max_usage_count}</td>
                      <td>{serial.current_usage_count}</td>
                      <td>
                        <span className={`badge ${
                          serial.usage_percentage >= 100 ? 'badge-danger' : 
                          serial.usage_percentage >= 80 ? 'badge-warning' : 'badge-success'
                        }`}>
                          {serial.usage_percentage}%
                        </span>
                      </td>
                      <td>{serial.user_count}</td>
                      <td>
                        <span className={`badge ${serial.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {serial.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{serial.expires_at ? new Date(serial.expires_at).toLocaleDateString() : '-'}</td>
                      <td>{new Date(serial.created_at).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          {/* View - always visible */}
                          <button
                            className="btn btn-primary"
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                            onClick={() => viewDetail(serial.id)}
                          >
                            View
                          </button>
                          
                          {/* Edit - only if has permission */}
                          {canEdit && (
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              onClick={() => startEdit(serial)}
                            >
                              Edit
                            </button>
                          )}
                          
                          {/* Reset - only if has permission */}
                          {canReset && (
                            <button
                              className="btn btn-danger"
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              onClick={() => startReset(serial)}
                              disabled={serial.current_usage_count === 0}
                            >
                              Reset
                            </button>
                          )}
                          
                          {/* History - always visible */}
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                            onClick={() => viewResetHistory(serial)}
                          >
                            History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit) || 1}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Reset Modal - only if has permission */}
      {showResetModal && canReset && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%'
          }}>
            <h2 style={{ color: '#e74c3c' }}>⚠️ Reset Serial Key</h2>
            <p style={{ marginBottom: '20px', color: '#7f8c8d' }}>
              Serial: <strong>{showResetModal.serial_number}</strong>
            </p>

            <div style={{ background: '#fff3cd', padding: '15px', borderRadius: '4px', marginBottom: '20px' }}>
              <strong>Warning:</strong> This action will:
              <ul style={{ marginTop: '10px', marginBottom: 0 }}>
                <li>Reset usage count to 0</li>
                <li>Unlink {showResetModal.user_count} user(s) from this serial</li>
                <li>Create an audit log for compliance (개인정보보호법)</li>
              </ul>
            </div>

            <form onSubmit={handleReset}>
              <div className="form-group">
                <label>Admin Username</label>
                <input
                  type="text"
                  className="form-control"
                  value={resetData.admin_username}
                  onChange={(e) => setResetData(prev => ({ ...prev, admin_username: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label>Reset Reason * (minimum 10 characters)</label>
                <textarea
                  className="form-control"
                  placeholder="Enter detailed reason for reset (e.g., 'Customer incorrectly registered, CS ticket #12345')"
                  value={resetData.reason}
                  onChange={(e) => setResetData(prev => ({ ...prev, reason: e.target.value }))}
                  rows="4"
                  required
                  minLength={10}
                />
                <small style={{ color: '#7f8c8d' }}>
                  {resetData.reason.length}/10 characters (required for audit trail)
                </small>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-danger">
                  Confirm Reset
                </button>
                <button 
                  type="button"
                  className="btn btn-secondary" 
                  onClick={() => setShowResetModal(null)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset History Modal - always visible */}
      {showResetHistory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '900px',
            maxHeight: '80vh',
            overflow: 'auto',
            width: '100%'
          }}>
            <h2>Reset History</h2>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              Serial: <strong>{showResetHistory.serial_number}</strong> | 
              Total Resets: <strong>{resetHistory.length}</strong>
            </p>

            {resetHistory.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                No reset history
              </p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Admin</th>
                      <th>IP Address</th>
                      <th>Users Affected</th>
                      <th>Previous Usage</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resetHistory.map((log) => (
                      <tr key={log.id}>
                        <td>{new Date(log.reset_date).toLocaleString()}</td>
                        <td>{log.admin_username}</td>
                        <td style={{ fontSize: '11px' }}>{log.admin_ip || '-'}</td>
                        <td>{log.users_affected}</td>
                        <td>{log.previous_usage}</td>
                        <td style={{ maxWidth: '300px', fontSize: '12px' }}>{log.reset_reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button 
              className="btn btn-secondary" 
              onClick={() => setShowResetHistory(null)}
              style={{ marginTop: '20px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal - always visible */}
      {selectedSerial && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            width: '100%'
          }}>
            <h2>Serial Key Detail</h2>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              {selectedSerial.serial_number}
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
              <div>
                <p><strong>Max Usage:</strong> {selectedSerial.max_usage_count}</p>
                <p><strong>Current Usage:</strong> {selectedSerial.current_usage_count}</p>
                <p><strong>Usage Rate:</strong> {selectedSerial.usage_percentage}%</p>
              </div>
              <div>
                <p><strong>Status:</strong> {selectedSerial.is_active ? 'Active' : 'Inactive'}</p>
                <p><strong>Expires:</strong> {selectedSerial.expires_at ? new Date(selectedSerial.expires_at).toLocaleString() : 'Never'}</p>
                <p><strong>Created:</strong> {new Date(selectedSerial.created_at).toLocaleString()}</p>
              </div>
            </div>

            <h3>Registered Users ({selectedSerial.users?.length || 0})</h3>
            {!selectedSerial.users || selectedSerial.users.length === 0 ? (
              <p style={{ color: '#7f8c8d', padding: '20px', textAlign: 'center' }}>No users registered yet</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Registered</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSerial.users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.email}</td>
                        <td>{user.name}</td>
                        <td>{new Date(user.registered_at).toLocaleDateString()}</td>
                        <td>
                          <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button 
              className="btn btn-secondary" 
              onClick={() => setSelectedSerial(null)}
              style={{ marginTop: '20px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SerialKeys;